export const GRADE_LABELS = ["A", "B", "C", "D", "E", "F"];

export function numberOrNull(value) {
  if (
    value == null ||
    typeof value === "boolean" ||
    String(value).trim() === ""
  )
    return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function rateOrNull(value) {
  const number = numberOrNull(value);
  return number != null && number >= 0 && number <= 1 ? number : null;
}

export function termKey(row) {
  return `${row.year}-${row.semester}`;
}

export function sortTerms(rows) {
  return [...rows].sort(
    (a, b) =>
      Number(a.year) - Number(b.year) || Number(a.semester) - Number(b.semester)
  );
}

// Missing observations remain gaps, rather than becoming zeroes or interpolated data.
export function alignTerms(rows, domain) {
  const byTerm = new Map(rows.map((row) => [termKey(row), row]));
  return domain.map(
    (term) =>
      byTerm.get(termKey(term)) || { year: term.year, semester: term.semester }
  );
}

export function distributionForRow(row, women = false) {
  if (!row) return null;
  const total = numberOrNull(row[women ? "total_women" : "total_students"]);
  if (total == null || total <= 0) return null;
  const distribution =
    row[women ? "gender_grade_distribution" : "grade_distribution"] || {};
  const counts = GRADE_LABELS.map((label) =>
    Math.max(0, numberOrNull(distribution[label]) ?? 0)
  );
  if (counts.some((count) => count > 0)) {
    const gradedTotal = counts.reduce((sum, count) => sum + count, 0);
    return {
      labels: GRADE_LABELS,
      counts,
      percentages: counts.map((count) => (count / gradedTotal) * 100),
      total: gradedTotal,
      passFail: false,
    };
  }
  const passRate = rateOrNull(row[women ? "pass_rate_women" : "pass_rate"]);
  if (passRate == null) return null;
  return {
    labels: ["Pass", "Fail"],
    counts: [passRate * total, (1 - passRate) * total],
    percentages: [passRate * 100, (1 - passRate) * 100],
    total,
    passFail: true,
  };
}

export function summaryForRow(row, excludeFails = true) {
  if (!row) return { average: null, failRate: null, count: null };
  const distribution = distributionForRow(row);
  const passRate = rateOrNull(row.pass_rate);
  return {
    average: distribution?.passFail
      ? null
      : numberOrNull(
          row[excludeFails ? "average_grade_no_fails" : "average_grade"]
        ),
    failRate: passRate == null ? null : 1 - passRate,
    count: numberOrNull(row.total_students),
  };
}

export function averageForRow(row, key) {
  const women = key.includes("women");
  if (!row || (women && !(numberOrNull(row.total_women) > 0))) return null;
  if (distributionForRow(row, women)?.passFail) return null;
  return numberOrNull(row[key]);
}

// Product warning thresholds, not tests of statistical significance.
export const SMALL_SAMPLE_LIMIT = 50;
export const VERY_SMALL_SAMPLE_LIMIT = 10;

export function sampleLevel(value) {
  const count = numberOrNull(value);
  if (count == null || count < 0) return "unknown";
  if (count === 0) return "empty";
  if (count < VERY_SMALL_SAMPLE_LIMIT) return "very-small";
  return count < SMALL_SAMPLE_LIMIT ? "small" : "standard";
}

export function metricSampleSize(
  row,
  mode,
  excludeFails = true,
  women = false
) {
  if (!row) return null;
  if (mode === "failRates")
    return numberOrNull(row[women ? "total_women" : "total_students"]);
  const distribution = distributionForRow(row, women);
  if (!distribution) return null;
  if (mode === "distribution") return distribution.total;
  if (distribution.passFail) return null;
  return distribution.counts.reduce(
    (sum, count, i) => sum + (excludeFails && i === 5 ? 0 : count),
    0
  );
}

export function hasAmbiguousTerms(rows, mode, selectedKey) {
  const scoped = rows.filter(
    (row) => mode !== "distribution" || termKey(row) === selectedKey
  );
  return new Set(scoped.map(termKey)).size !== scoped.length;
}

export function uniquePeriodRow(rows, key) {
  const matches = rows.filter((row) => termKey(row) === key);
  return matches.length === 1 ? matches[0] : undefined;
}

export function combineCourseSeries(items, mode) {
  for (const item of items) {
    if (hasAmbiguousTerms(item.rows || [], mode, item.course.selectedTermKey))
      return { labels: [], datasets: [], reason: "ambiguous-periods" };
  }
  const populated = items.filter((item) =>
    item.chartData?.datasets?.[0]?.data.some(
      (value) => numberOrNull(value) != null
    )
  );
  if (!populated.length) return { labels: [], datasets: [], reason: "no-data" };
  if (mode === "distribution") {
    const periods = new Set(
      populated.map((item) => item.course.selectedTermKey)
    );
    if (periods.size !== 1 || periods.has(null) || periods.has(undefined))
      return { labels: [], datasets: [], reason: "different-periods" };
    const scale = JSON.stringify(populated[0].chartData.labels);
    if (
      populated.some((item) => JSON.stringify(item.chartData.labels) !== scale)
    )
      return { labels: [], datasets: [], reason: "different-scales" };
  }
  const labels = populated[0].chartData.labels;
  if (
    mode !== "distribution" &&
    populated.some(
      (item) => JSON.stringify(item.chartData.labels) !== JSON.stringify(labels)
    )
  )
    return { labels: [], datasets: [], reason: "different-timelines" };
  const colors = [
    "#326348",
    "#386fac",
    "#b77e2e",
    "#905d91",
    "#b55f50",
    "#288787",
    "#6e7330",
    "#5f658c",
  ];
  const dashes = [[], [7, 4], [2, 3], [9, 3, 2, 3]];
  return {
    labels,
    reason: null,
    datasets: populated.map((item, index) => {
      const source = item.chartData.datasets[0];
      return {
        ...source,
        courseId: item.course.id,
        label: item.course.course_code,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        borderDash:
          mode === "distribution" ? [] : dashes[index % dashes.length],
        borderWidth: mode === "distribution" ? 0 : 2.5,
        fill: false,
        spanGaps: false,
        tension: 0,
        maxBarThickness: 36,
      };
    }),
  };
}

// Do not turn another course's spring observations into artificial gaps in an
// autumn-only series. Explicit null observations still break the line.
export function comparisonPlotData(model, items, mode) {
  if (mode === "distribution") return model;
  const periods = items[0]?.chartData.periods || [];
  const indexByTerm = new Map(
    periods.map((period, index) => [termKey(period), index])
  );
  return {
    labels: model.labels,
    datasets: model.datasets.map((dataset) => {
      const item = items.find((item) => item.course.id === dataset.courseId);
      const observations = sortTerms(item?.rows || [])
        .map((row) => ({ row, index: indexByTerm.get(termKey(row)) }))
        .filter((entry) => entry.index != null);
      return {
        ...dataset,
        data: observations.map(({ row, index }) => ({
          x: Number(row.year) * 4 + Number(row.semester),
          y: dataset.data[index],
          periodLabel: model.labels[index],
          sampleCount: dataset.sampleCounts?.[index],
        })),
        pointStyle: observations.map(
          ({ index }) => dataset.pointStyle?.[index] || "circle"
        ),
        pointRadius: observations.map(
          ({ index }) => dataset.pointRadius?.[index] ?? 2
        ),
      };
    }),
  };
}
